import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";

import MDTypography from "components/MDTypography";
import Breadcrumbs from "layouts/maps/customstyle/breadcrumb";
import Tabs from "@mui/material/Tabs"; 
import Tab from "@mui/material/Tab";   
import Grid from "@mui/material/Grid";
// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarDesktopMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 3 PRO React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
} from "context";

function DashboardNavbar({ absolute, light, isMini, year }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const {
    transparentNavbar,
    fixedNavbar,
    darkMode,
  } = controller;
  const [tabValue, setTabValue] = useState(0); // Added tabValue state
  const route = useLocation().pathname.split("/").slice(1);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Setting the tabValue based on the current route 
    if (location.pathname.startsWith(`/${year}/cities${year}`)) {
      setTabValue(0);
    } else if (location.pathname.startsWith(`/${year}/states${year}`)) {
      setTabValue(1);
    }
  }, [location.pathname]); // Run this effect when the route changes
  
  useEffect(() => {
    if(window.screen.width <= 1280) {
      setMiniSidenav(dispatch, true); // Ensure menu is always closed
    }
  }, []);
  
  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(
        dispatch,
        (fixedNavbar && window.scrollY === 0) || !fixedNavbar
      );
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  // Styles for the navbar icons
  const iconsStyle = ({
    palette: { dark, white, text },
    functions: { rgba },
  }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) =>
        navbar(theme, { transparentNavbar, absolute, light, darkMode })
      }
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" sx={(theme) => navbarRow(theme, { isMini })}>
          <IconButton
            sx={navbarDesktopMenu}
            onClick={() => {
              setMiniSidenav(dispatch, true); // Allow sidenav popup for the homepage
              navigate("/"); //navigate to the homepage
            }}
            size="small"
            disableRipple
          >
            <Icon fontSize="large" sx={iconsStyle}>
              map
            </Icon>
          </IconButton>
          <MDTypography
            variant="h5"
            fontWeight="medium"
            sx={{
              cursor: "pointer",
              ml: 1, // Add some margin between the icon and the text
              color: "#6c757d", // Slightly gray color to match the icon
            }}
            onClick={() => {
              setMiniSidenav(dispatch, false); // Allow sidenav popup for the homepage
            }}
          >
            Solar Sentiment
          </MDTypography>
          <MDTypography
            variant="h5"
            fontWeight="medium"
            sx={{
              ml: 1, // Add margin around the slash
              color: "#6c757d", // Slightly gray color to match the icon// Match the icon's color
            }}
          >
            /
          </MDTypography>
          <Breadcrumbs
            icon="home"
            title={route[0]} // Show only the first level
            route={route.slice(0, 1)} // Pass only the first level for the route
            light={light}
            sx={{
              fontSize: "1.25rem", // Match h5 size
              fontWeight: "medium", // Match h5 font weight
            }}
          />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox
              sx={{
                display: "flex",
                justifyContent: "center", // Center the tabs
              }}
            >
              <AppBar position="static" sx={{ boxShadow: "none" }}>
                <Grid item xs={6} sm={4} md={3}>
                  <AppBar position="static" sx={{ boxShadow: "none" }}>
                    <Tabs
                      value={tabValue}
                      onChange={(event, newValue) => {
                        setTabValue(newValue);
                        if (newValue === 0) {
                          setMiniSidenav(dispatch, false); // Ensure sidenav stays closed
                          navigate(`/${year}/cities${year}`); // Navigate to State page // ethan: update the year
                        } else if (newValue === 1) {
                          setMiniSidenav(dispatch, false); // Ensure sidenav stays closed
                          navigate(`/${year}/states${year}`); // Navigate to City page // ethan: update the year
                        }
                      }}
                      indicatorColor="primary"
                      textColor="primary"
                    >
                      <Tab
                        label="City View"
                        icon={
                          <Icon sx={{ mt: 0.626, mr: 1 }}>
                            location_city
                          </Icon>
                        }
                        value={0}
                        sx={{
                          px: 6, // Add padding horizontally for flexibility
                          py: 1, // Add padding vertically for spacing
                          textAlign: "center", // Keep text centered
                          fontSize: "1.05rem", // Slightly larger font size
                        }}
                      />
                      <Tab
                        label="State View"
                        icon={
                          <Icon sx={{ mt: 0.626, mr: 1 }}>
                            public
                          </Icon>
                        }
                        value={1}
                        sx={{
                          px: 6, // Add padding horizontally for flexibility
                          py: 1, // Add padding vertically for spacing
                          textAlign: "center", // Keep text centered
                          fontSize: "1.05rem", // Slightly larger font size
                        }}
                      />
                    </Tabs>
                  </AppBar>
                </Grid>
              </AppBar>
            </MDBox>
          </MDBox>
        )}

      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;